/** The hub tabs, in tab-bar order (M35 Option 1). Battle is the lobby itself (no sheet up). */
export type HubTab = "shop" | "towers" | "battle" | "gear" | "events";

/**
 * The tab a hub sheet belongs to, from its class list, so the tab bar lights the page on screen. A tower's upgrade
 * sheet and the research tree count as Towers; every currency's shop (the Shop pages, the Shops, the Market, Deals and
 * VIP, the War Campaign pass, Skins bought with gems) as Shop; the Events page and what it opens (an event's track, Endless, Free rewards,
 * Iron, the check-in) as Events. The ⚙ menu and what it opens (Settings, the Guide, Achievements, the Almanac, Debug),
 * the commander's sheet and dialogs leave Battle lit.
 */
export function tabForSheet(classes: readonly string[]): HubTab {
  const has = (...c: string[]) => c.some((x) => classes.includes(x));
  if (has("skins") && has("towers")) return "towers";            // Towers → Skins (the sheet carries both classes)
  if (has("towers", "upgrade", "research")) return "towers";
  if (has("inventory")) return "gear";
  if (has("shophub", "shop", "chests", "market", "shops", "deals", "vippage", "pass")) return "shop";
  if (has("evhub", "events", "endless", "freerewards", "iron", "checkin")) return "events";
  return "battle";
}

/**
 * The single-sheet rule: given the hub's sheets in DOM order (oldest first) and whether each is visible, the indices
 * of the ones to close — every visible sheet but the newest. A parked sheet (hidden with `visibility`, like Settings
 * while its Graphics sheet is up) is left alone: it comes back when the sheet on top of it closes.
 */
export function sheetsToClose(visible: readonly boolean[]): number[] {
  const newest = visible.lastIndexOf(true);
  const out: number[] = [];
  visible.forEach((v, i) => { if (v && i !== newest) out.push(i); });
  return out;
}

/**
 * Keeps the hub to one sheet at a time with a scrim under it. Sheets are opened from many places (the tab bar, the
 * commander chip, the battle card, the builder chip, main.ts handlers), so instead of routing every opener through one
 * function this watches the UI layer: a new sheet closes the one before it, and a tap on the scrim closes the sheet on
 * top (through its own close button, so the sheet's close logic runs). The scrim stops above the tab bar (hub.css
 * `#ui.hubtabs`), so the tab bar stays live: a tab tap swaps the page.
 */
export class SheetGuard {
  private readonly scrim: HTMLDivElement;
  private readonly observer: MutationObserver;

  constructor(private readonly parent: HTMLElement, private readonly onTop: (sheet: HTMLElement | null) => void) {
    this.scrim = document.createElement("div");
    this.scrim.className = "sheet-scrim hidden";
    this.scrim.onclick = () => this.closeTop();
    parent.append(this.scrim);
    this.observer = new MutationObserver(() => this.sync());
    // `style` too: a parked sheet coming back (visibility cleared) changes no child list.
    this.observer.observe(parent, { childList: true, subtree: true, attributes: true, attributeFilter: ["style"] });
  }

  private sheets(): HTMLElement[] {
    return [...this.parent.children].filter((e): e is HTMLElement => e instanceof HTMLElement && e.classList.contains("sheet"));
  }

  private top(): HTMLElement | null {
    const visible = this.sheets().filter((s) => s.style.visibility !== "hidden");
    return visible[visible.length - 1] ?? null;
  }

  private sync(): void {
    const all = this.sheets();
    for (const i of sheetsToClose(all.map((s) => s.style.visibility !== "hidden"))) all[i].remove();
    const top = this.top();
    if (top) {
      if (this.scrim.nextElementSibling !== top) this.parent.insertBefore(this.scrim, top);   // directly under the sheet
      this.scrim.classList.remove("hidden");
    } else this.scrim.classList.add("hidden");
    this.onTop(top);
  }

  /** Whether any hub sheet is up (BATTLE closes it before it plays). */
  hasSheet(): boolean { return this.top() !== null; }

  /** Closes the sheet on top (Battle, or the lit tab, in the tab bar; a tap on the scrim). */
  closeTop(): void {
    const top = this.top();
    if (!top) return;
    const close = top.querySelector<HTMLButtonElement>(".close");
    if (close) close.click();
    else top.remove();
  }

  dispose(): void {
    this.observer.disconnect();
    this.scrim.remove();
  }
}
